import React, { useState } from 'react';
import { db } from '@/src/services/db';
import { verifyResponseHash } from '@/src/utils/crypto';
import { FormMetadata, FormResponse, FormResponseValue, FieldType, SignatureMethod } from '../../types';
import { Filter, Download, Columns, Search, CheckSquare, Square, Eye, X, ChevronDown, Image as ImageIcon, FileJson, LayoutTemplate, Star, CircleDot, ShieldCheck, ShieldAlert, MapPin, RefreshCw, Edit, Trash2, CheckCircle2, Upload, Plus, AlertCircle, ArrowRight } from 'lucide-react';
import { FilterBar } from './SharedFilterBar';
import MapRenderer from './MapRenderer';
import SignatureInput from './SignatureInput';

import { useMemo } from 'react';


interface ResponseBankProps {
  forms: FormMetadata[];
  responses: FormResponse[];
}

const ResponseBank: React.FC<ResponseBankProps> = ({ forms, responses }) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [viewResponse, setViewResponse] = useState<FormResponse | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [activeTab, setActiveTab] = useState<'visual' | 'json' | 'edit'>('visual');
  const [verificationResult, setVerificationResult] = useState<'verified' | 'failed' | null>(null);
  
  const [editData, setEditData] = useState<Record<string, any>>({});
  const [isSaving, setIsSaving] = useState(false);
  
  // UI State
  const [showFilters, setShowFilters] = useState(false);
  const [showColumns, setShowColumns] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [serverResults, setServerResults] = useState<FormResponse[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  // Advanced Filter State
  const [dynamicFilters, setDynamicFilters] = useState<Record<string, any>>({});
  const [selectedForms, setSelectedForms] = useState<string[]>(forms.map(f => f.id));
  const [selectedRespondent, setSelectedRespondent] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const respondents = useMemo(() => {
    const map = new Map<string, string>();
    responses.forEach(r => map.set(r.respondentId, r.respondentName));
    return Array.from(map.entries());
  }, [responses]);

  const formColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    const colors = ['#2563eb', '#f97316', '#10b981', '#ef4444', '#8b5cf6', '#06b6d4', '#f59e0b', '#ec4899'];
    forms.forEach((f, i) => map[f.id] = colors[i % colors.length]);
    return map;
  }, [forms]);

  const allFields = useMemo(() => {
    const fields: any[] = [];
    forms.forEach(f => {
      const activeVersion = f.versions?.find((v: any) => v.version === f.currentVersion);
      activeVersion?.schema?.forEach((field: any) => {
        fields.push({ ...field, formId: f.id, formTitle: f.title });
      });
    });
    return fields;
  }, [forms]);

  const updateDynamicFilter = (fieldId: string, key: string, val: any) => {
    setDynamicFilters(prev => {
      const n = { ...prev };
      if (!n[fieldId]) n[fieldId] = {};
      if (val === undefined || val === '') delete n[fieldId][key];
      else n[fieldId][key] = val;
      if (Object.keys(n[fieldId]).length === 0) delete n[fieldId];
      return n;
    });
  };

  const removeFieldFilter = (fieldId: string) => {
    setDynamicFilters(prev => { const n = { ...prev }; delete n[fieldId]; return n; });
  };

  const resetAllFilters = () => {
    setDynamicFilters({});
    setSelectedForms(forms.map(f => f.id));
    setSelectedRespondent('all');
    setStartDate('');
    setEndDate('');
    setFilterText('');
  };

  const hasActiveFilters = Object.keys(dynamicFilters).length > 0 || !!startDate || !!endDate || selectedRespondent !== 'all' || selectedForms.length !== forms.length || !!filterText;

  const accentColors = { bg: 'bg-indigo-600', hover: 'hover:bg-indigo-700', bgLight: 'bg-indigo-50', text: 'text-indigo-600', border: 'border-indigo-200' };


  
  // Column Visibility
  const [visibleColumns, setVisibleColumns] = useState<string[]>([
      'id', 'date', 'form', 'respondent', 'version', 'geo', 'status'
  ]);
  const [viewImageModal, setViewImageModal] = useState<string | null>(null);
  const [viewMapModal, setViewMapModal] = useState<{locations: any[], highlightedId?: string, label: string} | null>(null);

  // Saved Views
  interface SavedView {
    id: string;
    name: string;
    columns: string[];
    filters?: {
      dynamicFilters: Record<string, any>;
      selectedForms: string[];
      selectedRespondent: string;
      startDate: string;
      endDate: string;
      filterText: string;
    };
  }
  const [savedViews, setSavedViews] = useState<SavedView[]>([]);
  const [viewNameInput, setViewNameInput] = useState('');
  const [showSaveView, setShowSaveView] = useState(false);

  React.useEffect(() => {
    try {
      const views = localStorage.getItem('@asa-branca/response-views');
      if (views) setSavedViews(JSON.parse(views));
    } catch (e) {
      console.error("Failed to load saved views", e);
    }
  }, []);

  const saveCurrentView = () => {
    if (!viewNameInput.trim()) return;
    const newView = { 
        id: Date.now().toString(), 
        name: viewNameInput, 
        columns: [...visibleColumns],
        filters: {
            dynamicFilters: JSON.parse(JSON.stringify(dynamicFilters)),
            selectedForms: [...selectedForms],
            selectedRespondent,
            startDate,
            endDate,
            filterText
        }
    };
    const updated = [...savedViews, newView];
    setSavedViews(updated);
    localStorage.setItem('@asa-branca/response-views', JSON.stringify(updated));
    setViewNameInput('');
    setShowSaveView(false);
  };

  const updateView = (id: string) => {
    const updated = savedViews.map(v => {
        if (v.id === id) {
            return {
                ...v,
                columns: [...visibleColumns],
                filters: {
                    dynamicFilters: JSON.parse(JSON.stringify(dynamicFilters)),
                    selectedForms: [...selectedForms],
                    selectedRespondent,
                    startDate,
                    endDate,
                    filterText
                }
            };
        }
        return v;
    });
    setSavedViews(updated);
    localStorage.setItem('@asa-branca/response-views', JSON.stringify(updated));
  };

  const loadView = (id: string) => {
    const view = savedViews.find(v => v.id === id);
    if (view) {
        setVisibleColumns(view.columns);
        if (view.filters) {
            setDynamicFilters(view.filters.dynamicFilters || {});
            setSelectedForms(view.filters.selectedForms || forms.map(f => f.id));
            setSelectedRespondent(view.filters.selectedRespondent || 'all');
            setStartDate(view.filters.startDate || '');
            setEndDate(view.filters.endDate || '');
            setFilterText(view.filters.filterText || '');
        }
    }
  };

  const deleteView = (id: string) => {
    const updated = savedViews.filter(v => v.id !== id);
    setSavedViews(updated);
    localStorage.setItem('@asa-branca/response-views', JSON.stringify(updated));
  };

  // Custom Parameters
  const [customParams, setCustomParams] = useState<any[]>([]);
  React.useEffect(() => {
    try {
      const stored = localStorage.getItem('@forms-asa-branca/custom-parameters');
      if (stored) setCustomParams(JSON.parse(stored));
    } catch (e) {
      console.error(e);
    }
  }, []);

  const availableColumns = useMemo(() => {
      const base = [
          { id: 'id', label: 'ID', group: 'Sistema' },
          { id: 'date', label: 'Data', group: 'Sistema' },
          { id: 'form', label: 'Formulário', group: 'Sistema' },
          { id: 'respondent', label: 'Respondente', group: 'Sistema' },
          { id: 'version', label: 'Versão', group: 'Sistema' },
          { id: 'geo', label: 'Geolocalização', group: 'Sistema' },
          { id: 'status', label: 'Status Assinatura', group: 'Sistema' }
      ];

      const formFields = allFields.map(f => ({ id: f.id || f.fieldId, label: f.label, group: `Form: ${f.formTitle}` }));

      const params = customParams.map(p => ({ id: `param_${p.id}`, label: p.name, group: 'Parâmetros Customizados' }));

      const combined = [...base, ...formFields, ...params];

      // Deduplicate
      const seen = new Set();
      return combined.filter(c => {
          if (seen.has(c.id)) return false;
          seen.add(c.id);
          return true;
      });
  }, [allFields, customParams]);

  const groupedColumns = useMemo(() => {
      return availableColumns.reduce((acc, col) => {
          if (!acc[col.group]) acc[col.group] = [];
          acc[col.group].push(col);
          return acc;
      }, {} as Record<string, typeof availableColumns>);
  }, [availableColumns]);


  const toggleColumn = (colId: string) => {
      setVisibleColumns(prev => {
          if (prev.includes(colId)) return prev.filter(id => id !== colId);
          return [...prev, colId];
      });
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
      e.dataTransfer.setData('colIndex', index.toString());
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
      const dragIndex = Number(e.dataTransfer.getData('colIndex'));
      if (dragIndex === dropIndex) return;

      setVisibleColumns(prev => {
          const newCols = [...prev];
          const [draggedItem] = newCols.splice(dragIndex, 1);
          newCols.splice(dropIndex, 0, draggedItem);
          return newCols;
      });
  };

  const handleVerify = async () => {
      if (!viewResponse) return;
      if (!viewResponse.securityMetadata?.hash) {
          alert("Esta resposta não possui assinatura digital.");
          return;
      }
      const isValid = await verifyResponseHash(
          viewResponse.respondentId,
          viewResponse.securityMetadata.timestamp,
          viewResponse.data,
          viewResponse.securityMetadata.hash
      );
      setVerificationResult(isValid ? 'verified' : 'failed');
  };

  const handleSaveEdit = async () => {
      if (!viewResponse) return;
      setIsSaving(true);
      try {
          const userObj = { id: 'admin', name: 'Admin', tenantId: viewResponse.tenantId, role: 'admin', sectorIds: [], status: 'active' } as any; // Fake user for now, as useAuth is not here
          // Wait, we need to get user from useAuth if possible. Let's see if user is passed or if we can just pass a dummy one since db.ts logAction requires it.
          // Better: pass a dummy user or just 'Admin'
          const updated = await db.updateResponse(viewResponse.id, editData, userObj);
          setViewResponse(updated);
          setActiveTab('visual');
          setVerificationResult('failed'); // Hash is invalid now
          // Update in local state
          setServerResults(prev => prev.map(r => r.id === updated.id ? updated : r));
      } catch (err) {
          alert("Erro ao salvar edição.");
      } finally {
          setIsSaving(false);
      }
  };

  const toggleSelect = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setSelectedIds(newSet);
  };

  const toggleAll = () => {
    if (selectedIds.size === filteredResponses.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filteredResponses.map(r => r.id)));
  };

  const handleExportCSV = async () => {
      setIsExporting(true);
      try {
          // Extrai universo completo do backend em vez do cache visual restrito
          const filters = {
              forms: selectedForms.length < forms.length ? selectedForms : [],
              respondent: selectedRespondent,
              startDate: startDate,
              endDate: endDate
          };
          const serverData = await db.getAllResponsesForExport(filters);
          
          if (serverData.length === 0) {
              alert("Nenhum dado encontrado no servidor para os filtros selecionados.");
              setIsExporting(false);
              return;
          }

          const headers = ['ID', 'Data', 'Formulário', 'Respondente', 'Status', 'Dados JSON'];
          const rows = serverData.map(r => [
              r.id,
              new Date(r.submittedAt).toLocaleString(),
              forms.find(f => f.id === r.formId)?.title || 'N/A',
              r.respondentName,
              r.status,
              JSON.stringify(r.data).replace(/"/g, '""')
          ]);

          const csvContent = [
              headers.join(','),
              ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
          ].join('\n');

          // RN: Assinatura/Hash do arquivo inteiro
          const encoder = new TextEncoder();
          const dataBuffer = encoder.encode(csvContent);
          const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
          const hashArray = Array.from(new Uint8Array(hashBuffer));
          const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

          const finalCsvContent = csvContent + `\n\n--- FIM DOS DADOS ---\nHASH DE INTEGRIDADE (SHA-256): ${hashHex}`;

          const blob = new Blob([finalCsvContent], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `exportacao_respostas_audit_${new Date().toISOString()}.csv`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
      } catch (err) {
          console.error("Erro ao exportar:", err);
          alert("Ocorreu um erro ao extrair os dados do servidor.");
      } finally {
          setIsExporting(false);
      }
  };

  const handleServerSearch = async () => {
      try {
          const results = await db.searchResponsesFromServer(filterText);
          setServerResults(results);
      } catch (e) {
          alert("Erro na busca de servidor.");
      }
  };

  const allAvailableResponses = useMemo(() => {
      const map = new Map<string, FormResponse>();
      responses.forEach(r => map.set(r.id, r));
      serverResults.forEach(r => map.set(r.id, r));
      return Array.from(map.values());
  }, [responses, serverResults]);


  const filteredResponses = useMemo(() => {
    return allAvailableResponses.filter(r => {
      // 1. Text Search
      if (filterText) {
        const lower = filterText.toLowerCase();
        const formTitle = forms.find(f => f.id === r.formId)?.title.toLowerCase() || '';
        if (!(
          r.respondentName.toLowerCase().includes(lower) ||
          formTitle.includes(lower) ||
          r.id.toLowerCase().includes(lower)
        )) return false;
      }

      // 2. Forms
      if (selectedForms.length > 0 && !selectedForms.includes(r.formId)) return false;

      // 3. Respondent
      if (selectedRespondent !== 'all' && r.respondentId !== selectedRespondent) return false;

      // 4. Date Range
      if (startDate || endDate) {
        const d = new Date(r.startedAt).getTime();
        if (startDate && d < new Date(startDate).getTime()) return false;
        if (endDate && d > new Date(endDate).getTime() + 86400000) return false;
      }

      // 5. Dynamic Field Filters
      for (const [fieldId, filterObj] of Object.entries(dynamicFilters)) {
        if (!filterObj || Object.keys(filterObj).length === 0) continue;
        const ans = r.data?.[fieldId]?.value;
        const fieldConfig = allFields.find(f => f.fieldId === fieldId);
        if (!fieldConfig) continue;

        if (filterObj.notFilled === true) {
          if (ans !== undefined && ans !== null && ans !== '') return false;
          continue;
        } else if (ans === undefined || ans === null || ans === '') {
          if (filterObj.value || filterObj.min || filterObj.max) return false;
          continue;
        }

        if (['select', 'radio', 'yes_no'].includes(fieldConfig.fieldType)) {
          if (filterObj.value && String(ans) !== filterObj.value) return false;
        }
        if (['number', 'linear_scale', 'rating'].includes(fieldConfig.fieldType)) {
          const numAns = Number(ans);
          if (isNaN(numAns)) return false;
          if (filterObj.min !== '' && filterObj.min !== undefined && numAns < Number(filterObj.min)) return false;
          if (filterObj.max !== '' && filterObj.max !== undefined && numAns > Number(filterObj.max)) return false;
        }
        if (['text', 'text_long'].includes(fieldConfig.fieldType)) {
          const strAns = String(ans).toLowerCase();
          if (filterObj.value && filterObj.value.trim() !== '' && !strAns.includes(filterObj.value.toLowerCase())) return false;
        }
      }

      return true;
    }).sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime());
  }, [allAvailableResponses, forms, filterText, selectedForms, selectedRespondent, startDate, endDate, dynamicFilters, allFields]);

  // Helper to render field value in Visual Tab
  const renderVisualField = (fieldId: string, valueData: FormResponseValue, schema: any[]) => {
      const fieldDef = schema.find(f => f.id === fieldId);
      const label = fieldDef?.label || fieldId;
      const type = fieldDef?.type;
      
      if (valueData.value === 'REFUSED') {
          return (
              <div className="bg-red-50 border border-red-100 p-3 rounded-lg">
                  <span className="block text-xs font-bold text-red-500 uppercase mb-1">{label} (Recusado)</span>
                  <p className="text-red-700 italic text-sm">"{valueData.metadata?.refusalReason}"</p>
              </div>
          );
      }

      const content = () => {
          if (type === 'media_photo') {
              const images = Array.isArray(valueData.value) ? valueData.value : [valueData.value];
              return (
                  <div className="grid grid-cols-3 gap-2 mt-2">
                      {images.filter(Boolean).map((img: string, i: number) => (
                          <div key={i} className="relative aspect-square bg-slate-100 rounded-lg overflow-hidden border border-slate-200 cursor-pointer" onClick={() => setViewImageModal(img)}>
                              <img src={img} className="w-full h-full object-cover" alt={`Evidence ${i}`} />
                          </div>
                      ))}
                  </div>
              );
          }
          if (type === 'signature') {
               if (valueData.value === 'valid_token_mock') {
                   return <div className="bg-green-50 text-green-700 p-2 rounded text-sm font-mono border border-green-100">Assinado Digitalmente (Senha)</div>;
               }
               return (
                   <div className="mt-2 border border-slate-200 rounded-lg bg-white p-2 w-fit">
                       <img src={valueData.value} className="h-32 object-contain" alt="Signature" />
                   </div>
               );
          }

          // New Fields Visuals
          if (type === 'rating') {
              const val = Number(valueData.value);
              const max = fieldDef?.ratingConfig?.maxStars || 5;
              return (
                  <div className="flex gap-1 mt-1 text-yellow-400">
                      {[...Array(max)].map((_, i) => (
                          <Star key={i} size={20} fill={i < val ? "currentColor" : "none"} className={i < val ? "" : "text-slate-300"} />
                      ))}
                      <span className="text-slate-500 text-sm ml-2 font-mono">({val}/{max})</span>
                  </div>
              );
          }
          if (type === 'checkbox') {
              const vals = Array.isArray(valueData.value) ? valueData.value : [];
              return (
                  <div className="flex flex-wrap gap-2 mt-1">
                      {vals.map((v: string) => (
                          <span key={v} className="bg-slate-100 border border-slate-200 px-2 py-1 rounded text-sm text-slate-700 flex items-center gap-1">
                              <CheckSquare size={12}/> {fieldDef?.options?.find((o: any) => o.value === v)?.label || v}
                          </span>
                      ))}
                  </div>
              );
          }
           if (type === 'radio' || type === 'select') {
               const label = fieldDef?.options?.find((o: any) => o.value === valueData.value)?.label || valueData.value;
               return <div className="text-slate-800 font-medium mt-1 flex items-center gap-2"><CircleDot size={14} className="text-indigo-600"/> {label}</div>;
           }

          if (type === 'linear_scale') {
              return (
                <div className="flex items-center gap-2 mt-1">
                    <span className="text-2xl font-bold text-indigo-600">{valueData.value}</span>
                    <span className="text-xs text-slate-400 uppercase tracking-wide">
                        Escala {fieldDef?.linearScaleConfig?.min || 1} - {fieldDef?.linearScaleConfig?.max || 5}
                    </span>
                </div>
              );
          }

          if (type === 'yes_no') {
              return (
                  <div className="mt-1">
                      <span className={`px-3 py-1 rounded font-bold text-sm ${valueData.value === 'Sim' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {valueData.value}
                      </span>
                  </div>
              );
          }

          if (type === 'matrix') {
              const rows = valueData.value as any[] || [];
              const columns = fieldDef?.matrixConfig?.columns || [];
              return (
                  <div className="mt-2 overflow-x-auto border rounded border-slate-200">
                      <table className="w-full text-sm text-left">
                          <thead className="bg-slate-50 border-b border-slate-200">
                              <tr>
                                  {columns.map(col => (
                                      <th key={col.id} className="px-3 py-2 text-slate-500 font-semibold">{col.label}</th>
                                  ))}
                              </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                              {rows.map((row, i) => (
                                  <tr key={i}>
                                      {columns.map(col => (
                                          <td key={col.id} className="px-3 py-2 text-slate-700">
                                              {row[col.id] || '-'}
                                          </td>
                                      ))}
                                  </tr>
                              ))}
                          </tbody>
                      </table>
                  </div>
              );
          }

          if (type === 'location' || (valueData.value && typeof valueData.value === 'object' && 'lat' in valueData.value && 'lng' in valueData.value)) {
              return (
                  <button 
                      onClick={() => setViewMapModal({ locations: [{ lat: valueData.value.lat, lng: valueData.value.lng, id: '1', respondentName: 'Campo do Formulário' }], label: label })}
                      className="mt-1 flex items-center gap-2 text-indigo-600 hover:text-indigo-800 font-medium bg-indigo-50 px-3 py-2 rounded-lg border border-indigo-100"
                  >
                      <MapPin size={16} /> Ver Localização no Mapa
                  </button>
              );
          }

          return <p className="text-slate-800 text-sm font-medium">{String(valueData.value)}</p>;
      };

      return (
          <div className="bg-white border border-slate-100 p-4 rounded-lg shadow-sm">
              <span className="block text-xs font-bold text-slate-500 uppercase mb-1">{label}</span>
              {content()}
          </div>
      );
  };

  return (
    <div className="space-y-4 animate-in fade-in duration-300 relative">
      <div className="flex justify-between items-end">
        <div>
            <h2 className="text-2xl font-bold text-slate-800">Banco de Respostas</h2>
            <p className="text-sm text-slate-500">Auditoria forense de todos os dados coletados.</p>
        </div>
        <div className="flex gap-2 relative">
            <button 
                onClick={() => setShowFilters(!showFilters)}
                className={`px-3 py-1.5 border rounded text-sm flex items-center gap-2 transition-colors ${showFilters ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`}
            >
                <Filter size={14}/> Filtrar
            </button>
            <button 
                onClick={() => setShowColumns(!showColumns)}
                className={`px-3 py-1.5 border rounded text-sm flex items-center gap-2 transition-colors ${showColumns ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`}
            >
                <Columns size={14}/> Colunas
            </button>
            <button 
                onClick={handleExportCSV}
                disabled={isExporting}
                className={`px-3 py-1.5 bg-indigo-50 border border-indigo-200 text-indigo-700 rounded text-sm flex items-center gap-2 font-medium ${isExporting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-indigo-100'}`}
            >
                <Download size={14}/> {isExporting ? 'Processando (Servidor)...' : 'Exportar CSV'}
            </button>

            {/* Columns Dropdown Manager */}
            {showColumns && (
                <div className="absolute top-12 right-0 w-[450px] bg-white border border-slate-200 shadow-2xl rounded-xl p-4 z-50 animate-in fade-in zoom-in-95 duration-200 flex flex-col gap-4">
                    <div className="flex justify-between items-center border-b border-slate-100 pb-2">
                        <p className="text-sm font-bold text-slate-800 uppercase flex items-center gap-2"><Columns size={16}/> Gerenciar Colunas</p>
                        <button onClick={() => setShowColumns(false)} className="text-slate-400 hover:text-slate-600"><X size={16}/></button>
                    </div>

                    <div className="flex flex-col gap-2 max-h-[40vh] overflow-y-auto pr-2">
                        <p className="text-xs font-bold text-slate-500 uppercase">Colunas Ativas (Arraste para reordenar)</p>
                        {visibleColumns.map((colId, index) => {
                            const colDef = availableColumns.find(c => c.id === colId);
                            if (!colDef) return null;
                            return (
                                <div
                                    key={colId}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, index)}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={(e) => handleDrop(e, index)}
                                    className="flex items-center justify-between bg-slate-50 border border-slate-200 p-2 rounded cursor-move hover:border-indigo-300 transition-colors"
                                >
                                    <div className="flex items-center gap-2 text-sm text-slate-700">
                                        <span className="text-slate-400 cursor-move text-xl leading-none">⋮⋮</span>
                                        <span className="font-medium truncate max-w-[250px]" title={colDef.label}>{colDef.label}</span>
                                        <span className="text-[10px] bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded ml-1 truncate max-w-[100px]">{colDef.group}</span>
                                    </div>
                                    <button onClick={() => toggleColumn(colId)} className="text-red-400 hover:text-red-600 p-1">
                                        <X size={14}/>
                                    </button>
                                </div>
                            );
                        })}
                    </div>

                    <div className="border-t border-slate-100 pt-4 flex flex-col gap-2 max-h-[30vh] overflow-y-auto pr-2">
                        <p className="text-xs font-bold text-slate-500 uppercase">Adicionar Colunas</p>
                        {Object.entries(groupedColumns).map(([groupName, cols]) => (
                            <div key={groupName} className="mb-2">
                                <p className="text-[11px] font-bold text-indigo-400 mb-1">{groupName}</p>
                                <div className="grid grid-cols-2 gap-1">
                                    {cols.map(col => {
                                        const active = visibleColumns.includes(col.id);
                                        return (
                                            <div key={col.id} onClick={() => toggleColumn(col.id)} className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-xs ${active ? 'bg-indigo-50 text-indigo-700 opacity-50' : 'hover:bg-slate-50 text-slate-600'}`}>
                                                 {active ? <CheckSquare size={12} className="text-indigo-600"/> : <Square size={12} className="text-slate-300"/>}
                                                 <span className="truncate" title={col.label}>{col.label}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
      </div>

      {/* Filter Bar */}

      {showFilters && (
        <FilterBar
          filterText={filterText} setFilterText={setFilterText}
          filteredResponses={filteredResponses}
          allResponses={responses}
          mode="analytics"
          forms={forms}
          formColorMap={formColorMap}
          selectedForms={selectedForms}
          setSelectedForms={setSelectedForms}
          respondents={respondents}
          selectedRespondent={selectedRespondent}
          setSelectedRespondent={setSelectedRespondent}
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          dynamicFilters={dynamicFilters}
          updateDynamicFilter={updateDynamicFilter}
          removeFieldFilter={removeFieldFilter}
          allFields={allFields}
          hasActiveFilters={hasActiveFilters}
          resetAllFilters={resetAllFilters}
          accentColors={accentColors}
        />
      )}

      {/* Toolbar */}
      <div className="bg-white p-2 rounded-t-lg border border-slate-200 border-b-0 flex items-center justify-between">
         <div className="flex items-center gap-2">
            {/* Views Dropdown */}
            <div className="relative group">
                <button className="px-3 py-1.5 border border-slate-300 rounded text-sm flex items-center gap-2 bg-white text-slate-600 hover:bg-slate-50 transition-colors">
                    <Star size={14} className="text-yellow-500"/> Atalhos de Visualização <ChevronDown size={14}/>
                </button>
                <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-slate-200 shadow-xl rounded-lg p-2 hidden group-hover:block z-50">
                    <p className="text-xs font-bold text-slate-500 uppercase px-2 mb-2">Visualizações Salvas</p>
                    {savedViews.length === 0 && <p className="text-xs text-slate-400 px-2 italic">Nenhum atalho salvo.</p>}
                    {savedViews.map(view => (
                        <div key={view.id} className="flex items-center justify-between px-2 py-1.5 hover:bg-slate-50 rounded group/item">
                            <button onClick={() => loadView(view.id)} className="text-sm text-slate-700 hover:text-indigo-600 text-left flex-1 truncate" title="Carregar atalho">{view.name}</button>
                            <div className="flex items-center gap-1 opacity-0 group-hover/item:opacity-100 transition-opacity">
                                <button onClick={(e) => { e.stopPropagation(); updateView(view.id); }} className="text-slate-300 hover:text-blue-500 p-1 rounded hover:bg-blue-50" title="Atualizar (Sobrescrever atalho com filtros/colunas atuais)"><RefreshCw size={14}/></button>
                                <button onClick={(e) => { e.stopPropagation(); deleteView(view.id); }} className="text-slate-300 hover:text-red-500 p-1 rounded hover:bg-red-50" title="Excluir atalho"><X size={14}/></button>
                            </div>
                        </div>
                    ))}
                    <div className="border-t border-slate-100 mt-2 pt-2 px-2">
                        {showSaveView ? (
                            <div className="flex items-center gap-1">
                                <input type="text" autoFocus value={viewNameInput} onChange={e => setViewNameInput(e.target.value)} placeholder="Nome da visualização..." className="text-xs border border-slate-300 rounded px-2 py-1 w-full"/>
                                <button onClick={saveCurrentView} className="bg-indigo-600 text-white p-1 rounded hover:bg-indigo-700"><CheckSquare size={14}/></button>
                                <button onClick={() => setShowSaveView(false)} className="bg-slate-200 text-slate-600 p-1 rounded hover:bg-slate-300"><X size={14}/></button>
                            </div>
                        ) : (
                            <button onClick={() => setShowSaveView(true)} className="text-xs text-indigo-600 font-bold hover:underline w-full text-left">+ Salvar Visualização Atual</button>
                        )}
                    </div>
                </div>
            </div>

            <div className="w-px h-6 bg-slate-200 mx-1"></div>

             {!showFilters && (
                 <div className="relative flex items-center gap-2">
                    <div className="relative">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                        <input 
                            value={filterText}
                            onChange={e => {
                                setFilterText(e.target.value);
                                if (e.target.value === '') setServerResults([]); // Clear server results on empty
                            }}
                            className="pl-8 pr-4 py-1.5 bg-slate-50 border border-slate-200 rounded text-sm w-64 focus:ring-1 focus:ring-indigo-500" 
                            placeholder="Busca rápida..." 
                        />
                    </div>
                    {filterText && (
                        <button 
                            onClick={handleServerSearch}
                            className="text-xs font-bold text-indigo-600 hover:text-indigo-800 bg-indigo-50 px-2 py-1.5 rounded flex items-center gap-1 border border-indigo-100"
                        >
                           <Search size={12} /> Buscar no Servidor
                        </button>
                    )}
                </div>
             )}
         </div>
         <div className="text-xs text-slate-500 px-2">
             Exibindo {filteredResponses.length} registros
         </div>
      </div>

      {/* High Density Data Grid */}
      <div className="bg-white rounded-b-lg border border-slate-200 shadow-sm overflow-x-auto min-h-[300px]">
        <table className="w-full text-left border-collapse">
            <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                    <th className="px-3 py-2 w-10 text-center">
                        <button onClick={toggleAll} className="text-slate-400 hover:text-slate-600">
                            {selectedIds.size === filteredResponses.length && filteredResponses.length > 0 ? <CheckSquare size={16}/> : <Square size={16}/>}
                        </button>
                    </th>
                    {visibleColumns.map((colId, index) => {
                        const colDef = availableColumns.find(c => c.id === colId);
                        if (!colDef) return null;
                        return (
                            <th
                                key={colId}
                                className="px-3 py-2 text-xs font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap bg-slate-50"
                            >
                                {colDef.label}
                            </th>
                        );
                    })}
                    <th className="px-3 py-2 text-xs font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap text-right">Ações</th>
                </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-xs text-slate-700">
                {filteredResponses.map(r => {
                    const hasRefusal = Object.values(r.data).some((val: FormResponseValue) => val.value === 'REFUSED');
                    return (
                        <tr key={r.id} className={`hover:bg-slate-50 ${selectedIds.has(r.id) ? 'bg-indigo-50/50' : ''}`}>
                            <td className="px-3 py-2 text-center whitespace-nowrap">
                                <button onClick={() => toggleSelect(r.id)} className={`${selectedIds.has(r.id) ? 'text-indigo-600' : 'text-slate-300'}`}>
                                    {selectedIds.has(r.id) ? <CheckSquare size={16}/> : <Square size={16}/>}
                                </button>
                            </td>
                            {visibleColumns.map(colId => {
                                if (colId === 'id') return <td key={colId} className="px-3 py-2 font-mono text-slate-500 whitespace-nowrap">{r.id.split('-')[1]}</td>;
                                if (colId === 'date') return <td key={colId} className="px-3 py-2 whitespace-nowrap">{new Date(r.submittedAt).toLocaleDateString()} <span className="text-slate-400">{new Date(r.submittedAt).toLocaleTimeString()}</span></td>;
                                if (colId === 'form') return <td key={colId} className="px-3 py-2 font-medium whitespace-nowrap">{forms.find(f => f.id === r.formId)?.title}</td>;
                                if (colId === 'respondent') return <td key={colId} className="px-3 py-2 whitespace-nowrap">{r.respondentName}</td>;
                                if (colId === 'version') return <td key={colId} className="px-3 py-2 text-center whitespace-nowrap"><span className="bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200 font-mono">v{r.formVersion}</span></td>;
                                if (colId === 'geo') {
                                    const geo = r.deviceInfo?.geolocation;
                                    if (geo && geo.lat && geo.lng) {
                                        return (
                                            <td key={colId} className="px-3 py-2 font-mono text-slate-400 whitespace-nowrap">
                                                <button onClick={(e) => {
                                                    e.stopPropagation();
                                                    const allLocs = filteredResponses.map(fr => {
                                                        const d = fr.deviceInfo?.geolocation;
                                                        if (d && d.lat && d.lng) return { lat: d.lat, lng: d.lng, id: fr.id, respondentName: fr.respondentName };
                                                        return null;
                                                    }).filter(Boolean);
                                                    setViewMapModal({ locations: allLocs, highlightedId: r.id, label: 'Geolocalização (IP)' });
                                                }} className="text-indigo-600 hover:underline flex items-center gap-1">
                                                    <CircleDot size={12}/> Ver Mapa
                                                </button>
                                            </td>
                                        );
                                    }
                                    return <td key={colId} className="px-3 py-2 font-mono text-slate-400 whitespace-nowrap">{r.deviceInfo?.ip || '192.168.1.X'}</td>;
                                }
                                if (colId === 'status') return <td key={colId} className="px-3 py-2 whitespace-nowrap">{r.status === 'rejected' ? <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-800">Recusado</span> : <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-800">Recebido</span>}</td>;

                                // Params handling (simplified for display)
                                if (colId.startsWith('param_')) {
                                    // In a real scenario, this would evaluate the parameter engine. For now, try to read from r if injected, else display N/A or '-'
                                    const val = (r as any)[`c_${colId.replace('param_', '')}`] ?? '-';
                                    const strVal = String(val);
                                    if (strVal && strVal.includes(',')) {
                                        const parts = strVal.split(',');
                                        if (parts.length === 2 && !isNaN(Number(parts[0])) && !isNaN(Number(parts[1]))) {
                                            return <td key={colId} className="px-3 py-2 whitespace-nowrap"><button onClick={() => setViewMapModal({ locations: [{ lat: Number(parts[0]), lng: Number(parts[1]) }], label: availableColumns.find(c => c.id === colId)?.label || colId })} className="text-indigo-600 hover:underline">Ver Mapa</button></td>;
                                        }
                                    }
                                    return <td key={colId} className="px-3 py-2 whitespace-nowrap">{strVal}</td>;
                                }

                                // Form fields
                                const valData = r.data[colId];
                                if (!valData) return <td key={colId} className="px-3 py-2 whitespace-nowrap text-slate-400">-</td>;
                                if (valData.value === 'REFUSED') return <td key={colId} className="px-3 py-2 whitespace-nowrap text-red-500">Recusado</td>;

                                const fieldDef = allFields.find(f => f.id === colId || f.fieldId === colId);
                                if (fieldDef?.type === 'media_photo' || fieldDef?.fieldType === 'media_photo') {
                                    const imgs = Array.isArray(valData.value) ? valData.value : [valData.value];
                                    return (
                                        <td key={colId} className="px-3 py-2 whitespace-nowrap">
                                            <div className="flex gap-1">
                                                {imgs.filter(Boolean).map((img: string, i: number) => (
                                                    <img key={i} src={img} onClick={(e) => { e.stopPropagation(); setViewImageModal(img); }} className="w-8 h-8 object-cover rounded cursor-pointer border border-slate-200" alt="Evidência" />
                                                ))}
                                            </div>
                                        </td>
                                    );
                                }
                                if (fieldDef?.type === 'location' || fieldDef?.fieldType === 'location') {
                                    if (valData.value && valData.value.lat && valData.value.lng) {
                                        return (
                                            <td key={colId} className="px-3 py-2 whitespace-nowrap">
                                                <button onClick={(e) => {
                                                    e.stopPropagation();
                                                    // Gather all locations for this column
                                                    const allLocs = filteredResponses.map(fr => {
                                                        const d = fr.data[colId]?.value;
                                                        if (d && d.lat && d.lng) return { lat: d.lat, lng: d.lng, id: fr.id, respondentName: fr.respondentName };
                                                        return null;
                                                    }).filter(Boolean);
                                                    setViewMapModal({ locations: allLocs, highlightedId: r.id, label: fieldDef.label });
                                                }} className="text-indigo-600 hover:underline">Ver no Mapa</button>
                                            </td>
                                        );
                                    }
                                }

                                let displayVal = valData.value;
                                if (Array.isArray(displayVal)) displayVal = displayVal.join(', ');
                                else if (typeof displayVal === 'object') displayVal = JSON.stringify(displayVal);

                                return <td key={colId} className="px-3 py-2 whitespace-nowrap max-w-[200px] truncate" title={String(displayVal)}>{String(displayVal)}</td>;
                            })}
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                                <button
                                    onClick={async () => {
                                        setViewResponse(r);
                                        setVerificationResult(null);
                                        setActiveTab('visual');
                                        setIsLoadingDetails(true);
                                        const full = await db.getResponseById(r.id);
                                        if (full) setViewResponse(full);
                                        setIsLoadingDetails(false);
                                    }}
                                    className="text-indigo-600 hover:underline flex items-center justify-end gap-1"
                                >
                                    <Eye size={14}/> Ver
                                </button>
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
        {filteredResponses.length === 0 && <div className="p-8 text-center text-slate-400">Nenhum dado encontrado para os filtros atuais.</div>}
      </div>


      {/* MAP MODAL */}
      {viewMapModal && (
          <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-[60] p-6" onClick={() => setViewMapModal(null)}>
              <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl h-[80vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
                  <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                      <div>
                          <h3 className="font-bold text-lg text-slate-800">Visualização de Mapa</h3>
                          <p className="text-xs text-slate-500">Localizações para: {viewMapModal.label}</p>
                      </div>
                      <button onClick={() => setViewMapModal(null)} className="text-slate-400 hover:text-slate-600"><X size={20}/></button>
                  </div>
                  <div className="flex-1 relative bg-slate-100">
                      <React.Suspense fallback={<div className="flex items-center justify-center h-full">Carregando Mapa...</div>}>
                          <MapRenderer
                              filteredResponses={filteredResponses}
                              forms={forms}
                              formColorMap={formColorMap}
                              highlightedResponseId={viewMapModal.highlightedId}
                              setHighlightedResponseId={() => {}}
                              flyToPos={viewMapModal.locations.find(l => l.id === viewMapModal.highlightedId) ? [viewMapModal.locations.find(l => l.id === viewMapModal.highlightedId).lat, viewMapModal.locations.find(l => l.id === viewMapModal.highlightedId).lng] : (viewMapModal.locations[0] ? [viewMapModal.locations[0].lat, viewMapModal.locations[0].lng] : null)}
                              mapLayer={{ url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png' }}
                              pinLegend1={{}}
                              pinLegend2={{}}
                              getLegendDisplay={() => null}
                              setSelectedResponseId={() => {}}
                          />
                      </React.Suspense>
                  </div>
              </div>
          </div>
      )}

      {/* FULLSCREEN IMAGE MODAL */}
      {viewImageModal && (
          <div className="fixed inset-0 bg-slate-900/90 flex items-center justify-center z-[70] p-6" onClick={() => setViewImageModal(null)}>
              <div className="relative max-w-full max-h-full flex items-center justify-center" onClick={e => e.stopPropagation()}>
                  <button onClick={() => setViewImageModal(null)} className="absolute -top-10 right-0 text-white hover:text-slate-300"><X size={32}/></button>
                  <img src={viewImageModal} className="max-w-full max-h-[90vh] object-contain rounded shadow-2xl" alt="Visualização em tamanho real" />
              </div>
          </div>
      )}

        {/* VIEW DETAILS MODAL */}
        {viewResponse && (
            <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-6" onClick={() => setViewResponse(null)}>
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
                    
                    {/* Header */}
                    <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                        <div>
                            <h3 className="font-bold text-lg text-slate-800">Detalhes da Resposta <span className="text-slate-400 font-mono text-sm ml-2">#{viewResponse.id.split('-')[1]}</span></h3>
                            <p className="text-xs text-slate-500">Enviado por {viewResponse.respondentName} em {new Date(viewResponse.submittedAt).toLocaleString()}</p>
                        </div>
                        <button onClick={() => setViewResponse(null)} className="text-slate-400 hover:text-slate-600"><X size={20}/></button>
                    </div>

                    {/* Tabs */}
                    <div className="flex border-b border-slate-200">
                        <button 
                            onClick={() => setActiveTab('visual')}
                            className={`flex-1 py-3 text-sm font-bold flex items-center justify-center gap-2 ${activeTab === 'visual' ? 'text-indigo-600 border-b-2 border-indigo-600 bg-white' : 'text-slate-500 bg-slate-50 hover:bg-slate-100'}`}
                        >
                            <LayoutTemplate size={16}/> Visualização Rica
                        </button>
                        <button 
                            onClick={() => setActiveTab('json')}
                            className={`flex-1 py-3 text-sm font-bold flex items-center justify-center gap-2 ${activeTab === 'json' ? 'text-indigo-600 border-b-2 border-indigo-600 bg-white' : 'text-slate-500 bg-slate-50 hover:bg-slate-100'}`}
                        >
                            <FileJson size={16}/> JSON Bruto
                        </button>
                        {forms.find(f => f.id === viewResponse.formId)?.permitir_edicao && (
                           <button 
                               onClick={() => { setActiveTab('edit'); setEditData(JSON.parse(JSON.stringify(viewResponse.data))); }}
                               className={`flex-1 py-3 text-sm font-bold flex items-center justify-center gap-2 ${activeTab === 'edit' ? 'text-indigo-600 border-b-2 border-indigo-600 bg-white' : 'text-slate-500 bg-slate-50 hover:bg-slate-100'}`}
                           >
                               <Edit size={16}/> Editar Resposta
                           </button>
                        )}
                    </div>

                    {/* Content */}
                    <div className="p-6 overflow-y-auto flex-1 bg-slate-50">
                        {isLoadingDetails ? (
                             <div className="flex items-center justify-center h-full text-slate-400 gap-2">
                                <div className="w-4 h-4 border-2 border-slate-300 border-t-indigo-600 rounded-full animate-spin"></div>
                                Carregando detalhes completos...
                            </div>
                        ) : activeTab === 'json' ? (
                            <div className="font-mono text-xs text-slate-700 bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
                                <pre className="whitespace-pre-wrap">{JSON.stringify(viewResponse, null, 2)}</pre>
                            </div>
                        ) : activeTab === 'edit' ? (
                            <div className="space-y-4 max-w-2xl mx-auto">
                                <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mb-4 text-sm text-yellow-800">
                                    <strong>Modo de Edição Administrativa</strong>: Alterar estes dados invalidará a assinatura digital da resposta original.
                                </div>
                                {(() => {
                                    const formMeta = forms.find(f => f.id === viewResponse.formId);
                                    const versionSchema = formMeta?.versions.find(v => v.version === viewResponse.formVersion)?.schema || [];
                                    const dataKeys = Object.keys(editData);
                                    
                                    const schemaIds = versionSchema.map(f => f.id);
                                    const ordered = [
                                        ...schemaIds.filter(id => id in editData),
                                        ...dataKeys.filter(id => !schemaIds.includes(id))
                                    ];

                                    return ordered.map(fieldId => {
                                        const fieldDef = versionSchema.find(f => f.id === fieldId);
                                        const label = fieldDef?.label || fieldId;
                                        const valData = editData[fieldId] || { value: '' };

                                        const handleValChange = (newVal: any) => {
                                            setEditData(prev => ({
                                                ...prev,
                                                [fieldId]: { ...valData, value: newVal }
                                            }));
                                        };

                                        const isRefused = valData.value === 'REFUSED';

                                        // Handlers from FormResponder
                                        const handleCheckboxChange = (val: string) => {
                                            const currentValues = (valData.value as string[]) || [];
                                            let newValues;
                                            if (currentValues.includes(val)) {
                                                newValues = currentValues.filter(v => v !== val);
                                            } else {
                                                newValues = [...currentValues, val];
                                            }
                                            handleValChange(newValues.length > 0 ? newValues : null);
                                        };

                                        const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, allowMultiple: boolean) => {
                                            const files = e.target.files;
                                            if (files && files.length > 0) {
                                                const readers: Promise<string>[] = [];
                                                for (let i = 0; i < files.length; i++) {
                                                    readers.push(new Promise((resolve) => {
                                                        const reader = new FileReader();
                                                        reader.onloadend = () => resolve(reader.result as string);
                                                        reader.readAsDataURL(files[i]);
                                                    }));
                                                }
                                                Promise.all(readers).then(results => {
                                                    if (allowMultiple) {
                                                        const existing = valData.value || [];
                                                        const currentArray = Array.isArray(existing) ? existing : [existing].filter(Boolean);
                                                        handleValChange([...currentArray, ...results]);
                                                    } else {
                                                        handleValChange(results[0]);
                                                    }
                                                });
                                            }
                                        };

                                        const removeMediaItem = (index: number) => {
                                            const existing = valData.value;
                                            if (Array.isArray(existing)) {
                                                const newArray = [...existing];
                                                newArray.splice(index, 1);
                                                handleValChange(newArray.length > 0 ? newArray : null);
                                            } else {
                                                handleValChange(null);
                                            }
                                        };

                                        const handleRefusal = (reason: string) => {
                                            setEditData(prev => ({
                                                ...prev,
                                                [fieldId]: { 
                                                    value: 'REFUSED', 
                                                    metadata: { refusalReason: reason, timestamp: new Date().toISOString() } 
                                                }
                                            }));
                                        };

                                        return (
                                            <div key={fieldId} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm space-y-3">
                                                {!isRefused && fieldDef?.type !== FieldType.SIGNATURE && (
                                                    <label className="block text-sm font-bold text-slate-800 uppercase">
                                                        {label} {fieldDef?.required && <span className="text-red-500">*</span>}
                                                    </label>
                                                )}

                                                <div className="relative">
                                                    {isRefused ? (
                                                        <div className="bg-red-50 border border-red-200 p-4 rounded-xl flex flex-col gap-2">
                                                            <div className="text-red-800 font-semibold flex items-center gap-2">
                                                                <AlertCircle size={20}/> Assinatura Recusada
                                                            </div>
                                                            <p className="text-sm text-red-600 italic">"{valData.metadata?.refusalReason}"</p>
                                                            <button 
                                                                type="button" 
                                                                onClick={() => handleValChange(null)} 
                                                                className="text-sm text-blue-600 underline self-start mt-1"
                                                            >
                                                                Desfazer Recusa
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            {(!fieldDef || fieldDef.type === FieldType.TEXT_SHORT) && (
                                                                <input 
                                                                    type="text"
                                                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl p-4 text-base text-slate-900 focus:ring-2 focus:ring-indigo-500"
                                                                    value={valData.value || ''}
                                                                    onChange={(e) => handleValChange(e.target.value)}
                                                                />
                                                            )}
                                                            
                                                            {fieldDef?.type === FieldType.TEXT_LONG && (
                                                                <textarea 
                                                                    rows={4}
                                                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl p-4 text-base text-slate-900 focus:ring-2 focus:ring-indigo-500"
                                                                    value={valData.value || ''}
                                                                    onChange={(e) => handleValChange(e.target.value)}
                                                                />
                                                            )}
                                                            
                                                            {fieldDef?.type === FieldType.NUMBER && (
                                                                <input 
                                                                    type="number" 
                                                                    inputMode="numeric"
                                                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl p-4 text-xl font-mono text-slate-900 focus:ring-2 focus:ring-indigo-500"
                                                                    value={valData.value || ''}
                                                                    onChange={(e) => handleValChange(parseFloat(e.target.value))}
                                                                />
                                                            )}

                                                            {fieldDef?.type === FieldType.DATE && (
                                                                <input 
                                                                    type="date" 
                                                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl p-4 text-base text-slate-900 focus:ring-2 focus:ring-indigo-500"
                                                                    value={valData.value || ''}
                                                                    onChange={(e) => handleValChange(e.target.value)}
                                                                />
                                                            )}

                                                            {fieldDef?.type === FieldType.YES_NO && (
                                                                <div className="flex gap-4">
                                                                    <button type="button" onClick={() => handleValChange('Sim')} className={`flex-1 py-4 rounded-xl border-2 font-bold transition-all ${valData.value === 'Sim' ? 'border-green-500 bg-green-50 text-green-700' : 'border-slate-200 text-slate-500'}`}>SIM</button>
                                                                    <button type="button" onClick={() => handleValChange('Não')} className={`flex-1 py-4 rounded-xl border-2 font-bold transition-all ${valData.value === 'Não' ? 'border-red-500 bg-red-50 text-red-700' : 'border-slate-200 text-slate-500'}`}>NÃO</button>
                                                                </div>
                                                            )}

                                                            {fieldDef?.type === FieldType.RATING && (
                                                                <div className="flex justify-start gap-2 py-2">
                                                                    {[...Array(fieldDef.ratingConfig?.maxStars || 5)].map((_, i) => (
                                                                        <button key={i} type="button" onClick={() => handleValChange(i + 1)} className={`p-2 transition-transform active:scale-125 ${(valData.value || 0) > i ? 'text-yellow-400' : 'text-slate-300'}`}>
                                                                            <Star size={36} fill="currentColor" strokeWidth={1} />
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            )}

                                                            {fieldDef?.type === FieldType.RADIO && (
                                                                <div className="space-y-3">
                                                                    {fieldDef.options?.map((opt) => (
                                                                        <label key={opt.value} className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all cursor-pointer ${valData.value === opt.value ? 'border-indigo-500 bg-indigo-50' : 'border-slate-100 bg-slate-50'}`}>
                                                                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${valData.value === opt.value ? 'border-indigo-500' : 'border-slate-300'}`}>
                                                                                {valData.value === opt.value && <div className="w-3 h-3 bg-indigo-500 rounded-full"></div>}
                                                                            </div>
                                                                            <input type="radio" name={fieldId} value={opt.value} className="hidden" onChange={() => handleValChange(opt.value)} />
                                                                            <span className={`text-base font-medium ${valData.value === opt.value ? 'text-indigo-900' : 'text-slate-700'}`}>{opt.label}</span>
                                                                        </label>
                                                                    ))}
                                                                </div>
                                                            )}

                                                            {fieldDef?.type === FieldType.CHECKBOX && (
                                                                <div className="space-y-3">
                                                                    {fieldDef.options?.map((opt) => {
                                                                        const isChecked = (valData.value || []).includes(opt.value);
                                                                        return (
                                                                            <label key={opt.value} className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all cursor-pointer ${isChecked ? 'border-indigo-500 bg-indigo-50' : 'border-slate-100 bg-slate-50'}`}>
                                                                                <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${isChecked ? 'border-indigo-500 bg-indigo-500 text-white' : 'border-slate-300'}`}>
                                                                                    {isChecked && <CheckCircle2 size={16} />}
                                                                                </div>
                                                                                <input type="checkbox" value={opt.value} className="hidden" onChange={() => handleCheckboxChange(opt.value)} />
                                                                                <span className={`text-base font-medium ${isChecked ? 'text-indigo-900' : 'text-slate-700'}`}>{opt.label}</span>
                                                                            </label>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}

                                                            {fieldDef?.type === FieldType.LINEAR_SCALE && (
                                                                <div className="py-2">
                                                                    <div className="flex justify-between text-xs font-bold text-slate-400 mb-2 uppercase px-1">
                                                                        <span>{fieldDef.linearScaleConfig?.minLabel}</span>
                                                                        <span>{fieldDef.linearScaleConfig?.maxLabel}</span>
                                                                    </div>
                                                                    <div className="flex justify-between items-center bg-slate-50 p-2 rounded-xl border border-slate-100">
                                                                        {Array.from({ length: (fieldDef.linearScaleConfig?.max || 5) - (fieldDef.linearScaleConfig?.min || 1) + 1 }, (_, i) => {
                                                                            const val = (fieldDef.linearScaleConfig?.min || 1) + i;
                                                                            const isSelected = valData.value === val;
                                                                            return (
                                                                                <button key={val} type="button" onClick={() => handleValChange(val)} className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-base transition-all ${isSelected ? 'bg-indigo-600 text-white shadow-lg scale-110' : 'text-slate-400 hover:bg-white hover:shadow'}`}>
                                                                                    {val}
                                                                                </button>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {fieldDef?.type === FieldType.SELECT && (
                                                                <div className="relative">
                                                                    <select className="w-full bg-slate-50 border border-slate-200 rounded-xl p-4 text-base text-slate-900 focus:ring-2 focus:ring-indigo-500 appearance-none" value={valData.value || ''} onChange={(e) => handleValChange(e.target.value)}>
                                                                        <option value="">Selecione...</option>
                                                                        {fieldDef.options?.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                                                    </select>
                                                                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                                                                        <ArrowRight size={20} className="rotate-90"/>
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {fieldDef?.type === FieldType.MEDIA_PHOTO && (
                                                                <div className="space-y-4">
                                                                    {valData.value && (Array.isArray(valData.value) || typeof valData.value === 'string') && (
                                                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                                                            {(Array.isArray(valData.value) ? valData.value : [valData.value]).map((img: string, idx: number) => (
                                                                                <div key={idx} className="relative rounded-xl overflow-hidden shadow-sm border border-slate-200 aspect-square">
                                                                                    <img src={img} alt={`Evidence ${idx}`} className="w-full h-full object-cover" />
                                                                                    <button type="button" onClick={() => removeMediaItem(idx)} className="absolute top-2 right-2 bg-white/90 p-1.5 rounded-full text-red-500 shadow-sm"><Trash2 size={16}/></button>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                    {(fieldDef.mediaConfig?.allowMultiple || !valData.value) && (
                                                                        <label className={`border-2 border-dashed border-slate-300 rounded-xl p-6 text-center bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer block ${valData.value ? 'p-4 flex items-center justify-center gap-2' : ''}`}>
                                                                            {valData.value ? (
                                                                                <><div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600"><Plus size={20} /></div><span className="font-semibold text-slate-600 text-sm">Adicionar Mais</span></>
                                                                            ) : (
                                                                                <><div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-2 shadow-sm text-indigo-500"><Upload size={24} /></div><span className="font-semibold text-slate-600 block text-sm">{fieldDef.mediaConfig?.allowMultiple ? 'Adicionar Fotos' : 'Enviar Foto'}</span></>
                                                                            )}
                                                                            <input type="file" accept="image/*" multiple={fieldDef.mediaConfig?.allowMultiple} className="hidden" onChange={(e) => handleFileUpload(e, fieldDef.mediaConfig?.allowMultiple || false)} />
                                                                        </label>
                                                                    )}
                                                                </div>
                                                            )}
                                                            
                                                            {fieldDef?.type === FieldType.SIGNATURE && fieldDef.signatureConfig && (
                                                                <SignatureInput 
                                                                    label={label}
                                                                    method={fieldDef.signatureConfig.method}
                                                                    allowRefusal={fieldDef.signatureConfig.allowRefusal}
                                                                    value={valData.value}
                                                                    onChange={(val) => handleValChange(val)}
                                                                    onRefusal={(reason) => handleRefusal(reason)}
                                                                />
                                                            )}

                                                            {fieldDef?.type === FieldType.MATRIX && (() => {
                                                                const handleMatrixChange = (rIdx: number, colId: string, val: string) => {
                                                                    const currentRows = (valData.value as any[]) || [];
                                                                    const newRows = [...currentRows];
                                                                    if (!newRows[rIdx]) newRows[rIdx] = {};
                                                                    newRows[rIdx][colId] = val;
                                                                    handleValChange(newRows);
                                                                };
                                                                const addMatrixRow = () => {
                                                                    const currentRows = (valData.value as any[]) || [];
                                                                    handleValChange([...currentRows, {}]);
                                                                };
                                                                const removeMatrixRow = (rIdx: number) => {
                                                                    const currentRows = (valData.value as any[]) || [];
                                                                    const newRows = [...currentRows];
                                                                    newRows.splice(rIdx, 1);
                                                                    handleValChange(newRows);
                                                                };
                                                                
                                                                return (
                                                                    <div className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden mt-2">
                                                                        <div className="overflow-x-auto">
                                                                            <table className="w-full text-sm text-left">
                                                                                <thead className="bg-slate-100 text-slate-600 font-bold uppercase text-xs">
                                                                                    <tr>
                                                                                        {fieldDef.matrixConfig?.columns.map(col => (
                                                                                            <th key={col.id} className="px-4 py-3 min-w-[120px]">{col.label}</th>
                                                                                        ))}
                                                                                        <th className="px-2 py-3 w-10"></th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody className="divide-y divide-slate-100">
                                                                                    {((valData.value as any[]) || []).map((row, rIdx) => (
                                                                                        <tr key={rIdx} className="bg-white">
                                                                                            {fieldDef.matrixConfig?.columns.map(col => (
                                                                                                <td key={col.id} className="p-2">
                                                                                                    {col.type === 'text' && (
                                                                                                        <input className="w-full border rounded p-2 text-slate-800 text-sm" value={row[col.id] || ''} onChange={e => handleMatrixChange(rIdx, col.id, e.target.value)} />
                                                                                                    )}
                                                                                                    {col.type === 'number' && (
                                                                                                        <input type="number" className="w-full border rounded p-2 text-slate-800 text-sm" value={row[col.id] || ''} onChange={e => handleMatrixChange(rIdx, col.id, e.target.value)} />
                                                                                                    )}
                                                                                                    {col.type === 'select' && (() => {
                                                                                                        const firstCol = fieldDef.matrixConfig?.columns[0];
                                                                                                        const shouldFilter = fieldDef.matrixConfig?.preventDuplicatesOnFirstColumn && firstCol?.id === col.id;
                                                                                                        const allRows = (valData.value as any[]) || [];
                                                                                                        const usedValues = shouldFilter ? new Set(allRows.filter((_, i) => i !== rIdx).map(r => r[col.id]).filter(Boolean)) : new Set<string>();
                                                                                                        return (
                                                                                                            <select className="w-full border rounded p-2 text-slate-800 bg-white text-sm" value={row[col.id] || ''} onChange={e => handleMatrixChange(rIdx, col.id, e.target.value)}>
                                                                                                                <option value="">...</option>
                                                                                                                {col.options?.filter(opt => !usedValues.has(opt)).map(opt => (
                                                                                                                    <option key={opt} value={opt}>{opt}</option>
                                                                                                                ))}
                                                                                                            </select>
                                                                                                        );
                                                                                                    })()}
                                                                                                </td>
                                                                                            ))}
                                                                                            <td className="p-2 text-center">
                                                                                                <button type="button" onClick={() => removeMatrixRow(rIdx)} className="text-red-400 hover:text-red-600"><Trash2 size={16}/></button>
                                                                                            </td>
                                                                                        </tr>
                                                                                    ))}
                                                                                </tbody>
                                                                            </table>
                                                                        </div>
                                                                        <button type="button" onClick={addMatrixRow} className="w-full py-3 text-indigo-600 font-bold text-sm bg-slate-50 hover:bg-slate-100 border-t border-slate-200">+ Adicionar Linha</button>
                                                                    </div>
                                                                );
                                                            })()}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    });
                                })()}
                                <div className="pt-4 flex justify-end">
                                    <button 
                                        onClick={handleSaveEdit}
                                        disabled={isSaving}
                                        className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-6 rounded-lg shadow disabled:opacity-50 transition-colors"
                                    >
                                        {isSaving ? 'Salvando...' : 'Salvar Alterações'}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {viewResponse.deviceInfo?.geolocation && viewResponse.deviceInfo.geolocation.lat && viewResponse.deviceInfo.geolocation.lng && (
                                    <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
                                        <h4 className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-2"><MapPin size={16} className="text-indigo-600"/> Localização do Dispositivo (Captura de Fundo)</h4>
                                        <div className="h-48 rounded bg-slate-100 overflow-hidden relative border border-slate-200">
                                            <React.Suspense fallback={<div className="flex items-center justify-center h-full text-slate-400 text-sm">Carregando Mapa...</div>}>
                                                <MapRenderer
                                                    filteredResponses={[viewResponse]}
                                                    forms={forms}
                                                    formColorMap={formColorMap}
                                                    highlightedResponseId={viewResponse.id}
                                                    setHighlightedResponseId={() => {}}
                                                    flyToPos={[viewResponse.deviceInfo.geolocation.lat, viewResponse.deviceInfo.geolocation.lng]}
                                                    mapLayer={{ url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png' }}
                                                    pinLegend1={{}}
                                                    pinLegend2={{}}
                                                    getLegendDisplay={() => null}
                                                    setSelectedResponseId={() => {}}
                                                />
                                            </React.Suspense>
                                        </div>
                                    </div>
                                )}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {(() => {
                                        const formMeta = forms.find(f => f.id === viewResponse.formId);
                                        const versionSchema = formMeta?.versions.find(v => v.version === viewResponse.formVersion)?.schema || [];
                                        const data = viewResponse.data || {};
                                        // Iterate by schema order, then append any orphan keys not in schema
                                        const schemaIds = versionSchema.map(f => f.id);
                                        const ordered = [
                                            ...schemaIds.filter(id => id in data),
                                            ...Object.keys(data).filter(id => !schemaIds.includes(id))
                                        ];
                                        return ordered.map(fieldId => (
                                            <React.Fragment key={fieldId}>
                                                {renderVisualField(fieldId, data[fieldId] as FormResponseValue, versionSchema)}
                                            </React.Fragment>
                                        ));
                                    })()}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="p-4 border-t border-slate-100 bg-white flex justify-between items-center">
                         <div className="flex items-center gap-2">
                            {verificationResult === 'verified' && <span className="text-green-600 flex items-center gap-1 text-sm font-bold"><ShieldCheck size={16}/> Assinatura Válida</span>}
                            {verificationResult === 'failed' && <span className="text-red-600 flex items-center gap-1 text-sm font-bold"><ShieldAlert size={16}/> Assinatura Inválida!</span>}
                            {!verificationResult && viewResponse.securityMetadata && (
                                <button onClick={handleVerify} className="text-indigo-600 text-sm hover:underline font-medium">Verificar Integridade</button>
                            )}
                         </div>
                         <button onClick={() => setViewResponse(null)} className="px-6 py-2 bg-slate-900 text-white rounded-lg font-bold shadow-lg hover:bg-slate-800 transition-colors">
                             Fechar Detalhes
                         </button>
                    </div>
                </div>
            </div>
        )}
    </div>
  );
};

export default ResponseBank;